import { useEffect, useState } from 'react';
import { clearIdentity, fetchMe, getEmail, updateMe, createAgentKey, revokeAgentKey } from '../api';
import type { Me } from '../types';
import AdminSheet from './AdminSheet';
import PractoImportPanel from './PractoImportPanel';
import IvfRegisterPanel from './IvfRegisterPanel';

export default function MeSheet({
  onClose,
  onSignOut,
}: {
  onClose: () => void;
  onSignOut: () => void;
}) {
  const [adminOpen, setAdminOpen] = useState(false);
  const [practoOpen, setPractoOpen] = useState(false);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [me, setMe] = useState<Me | null>(null);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [agentKey, setAgentKey] = useState<string | null>(null);
  const [keyBusy, setKeyBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  // Check if user has admin access
  const hasAdminAccess = me?.role === 'super_admin' || me?.role === 'admin';
  const canManageAgentKey = me?.role === 'super_admin' || me?.role === 'admin' || me?.role === 'manager';

  useEffect(() => {
    fetchMe()
      .then((d) => {
        setMe(d.user);
        setName(d.user?.name ?? '');
        setPhone(d.user?.phone ?? '');
      })
      .catch((e) => setErr(e instanceof Error ? e.message : 'load failed'));
  }, []);

  async function save() {
    setSaving(true);
    try {
      const { user: updated } = await updateMe({ name, phone });
      setMe(updated);
    } catch (e) {
      alert(e instanceof Error ? e.message : 'save failed');
    } finally {
      setSaving(false);
    }
  }

  async function handleCreateKey() {
    setKeyBusy(true);
    try {
      const res = await createAgentKey();
      setAgentKey(res.key);
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to create agent key');
    } finally {
      setKeyBusy(false);
    }
  }

  async function handleRevokeKey() {
    if (!confirm('Revoke your agent key? Any connected agent using this key will immediately lose access.')) return;
    setKeyBusy(true);
    try {
      await revokeAgentKey();
      setAgentKey(null);
      alert('Agent key revoked');
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to revoke agent key');
    } finally {
      setKeyBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-30 bg-black/40" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="absolute inset-x-0 bottom-0 max-h-[85vh] overflow-y-auto rounded-t-3xl bg-white p-5 shadow-2xl"
      >
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-base font-semibold">My profile</h2>
          <button onClick={onClose} className="text-sm text-slate-500">
            Close
          </button>
        </div>

        {err && <p className="mb-3 rounded-lg bg-red-50 p-2 text-xs text-red-700">{err}</p>}

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-slate-600">Email</label>
            <div className="mt-1 rounded-lg bg-slate-100 px-3 py-2 text-sm text-slate-700">
              {me?.email ?? getEmail()}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600">Display name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Satish R."
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600">Phone (E.164)</label>
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+9198…"
              inputMode="tel"
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
          </div>

          <button
            onClick={save}
            disabled={saving}
            className="w-full rounded-lg bg-wechat-accent px-3 py-2.5 text-sm font-semibold text-white disabled:opacity-60"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>

          {canManageAgentKey && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3.5 space-y-2.5">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-slate-700">Agent key</span>
                <span className="text-[11px] text-slate-400">MCP Evidence</span>
              </div>

              {agentKey && (
                <div className="space-y-1.5 rounded-lg border border-amber-200 bg-amber-50 p-2.5">
                  <p className="text-xs font-medium text-amber-900">
                    Paste into your Claude/Codex/Antigravity connector. It will not be shown again.
                  </p>
                  <div className="flex items-center gap-2">
                    <input
                      readOnly
                      value={agentKey}
                      className="flex-1 rounded border border-amber-300 bg-white px-2 py-1 font-mono text-xs text-slate-800 select-all"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        void navigator.clipboard.writeText(agentKey);
                        setCopied(true);
                        setTimeout(() => setCopied(false), 2000);
                      }}
                      className="rounded bg-slate-800 px-2.5 py-1 text-xs font-medium text-white hover:bg-slate-700"
                    >
                      {copied ? 'Copied' : 'Copy'}
                    </button>
                  </div>
                </div>
              )}

              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={keyBusy}
                  onClick={handleCreateKey}
                  className="flex-1 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50"
                >
                  Create / replace key
                </button>
                <button
                  type="button"
                  disabled={keyBusy}
                  onClick={handleRevokeKey}
                  className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-medium text-rose-700 hover:bg-rose-100 disabled:opacity-50"
                >
                  Revoke
                </button>
              </div>
            </div>
          )}

          {/* Footfall: every counsellor/manager can upload Practo's daily export (matched by phone). */}
          <button
            onClick={() => setPractoOpen((v) => !v)}
            className="w-full rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-800"
            aria-expanded={practoOpen}
          >
            Daily Practo upload (footfall)
          </button>
          {practoOpen && <div className="rounded-lg border border-slate-200 bg-white p-2"><PractoImportPanel /></div>}
          <button
            onClick={() => setRegisterOpen((v) => !v)}
            className="w-full rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-800"
            aria-expanded={registerOpen}
          >
            Daily IVF counselling register
          </button>
          {registerOpen && <div className="rounded-lg border border-slate-200 bg-white p-2"><IvfRegisterPanel /></div>}

          {hasAdminAccess && (
            <button
              onClick={() => setAdminOpen(true)}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700"
            >
              Admin · Meta pages
            </button>
          )}

          <button
            onClick={() => {
              if (confirm('Sign out and clear local session?')) {
                clearIdentity();
                
                // Use the new authentication system logout
                onSignOut();
              }
            }}
            className="w-full rounded-lg bg-slate-100 px-3 py-2 text-sm font-medium text-slate-700"
          >
            Sign out
          </button>
        </div>
      </div>
      {adminOpen && <AdminSheet onClose={() => setAdminOpen(false)} />}
    </div>
  );
}
